import { createAnswerClassifier } from "./answerClassifier";

const PROCESSOR_NAME = "twilanswer-answer-energy";

// Runs on the audio thread so detection keeps working in background tabs.
// Input 0 is the recipient's audio, input 1 the rep's mic; posts RMS every ~20 ms.
const WORKLET_SOURCE = `
class AnswerEnergyProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.blockSize = Math.round(sampleRate * 0.02);
    this.remoteSum = 0;
    this.micSum = 0;
    this.count = 0;
  }
  process(inputs) {
    const remote = inputs[0] && inputs[0][0];
    const mic = inputs[1] && inputs[1][0];
    const length = (remote || mic || { length: 128 }).length;
    for (let i = 0; i < length; i++) {
      const r = remote ? remote[i] : 0;
      const m = mic ? mic[i] : 0;
      this.remoteSum += r * r;
      this.micSum += m * m;
    }
    this.count += length;
    if (this.count >= this.blockSize) {
      this.port.postMessage({
        remote: Math.sqrt(this.remoteSum / this.count),
        mic: Math.sqrt(this.micSum / this.count),
        ms: (this.count / sampleRate) * 1000,
      });
      this.remoteSum = 0;
      this.micSum = 0;
      this.count = 0;
    }
    return true;
  }
}
registerProcessor("${PROCESSOR_NAME}", AnswerEnergyProcessor);
`;

const moduleLoads = new WeakMap();

function loadWorklet(ctx) {
  if (!moduleLoads.has(ctx)) {
    const url = URL.createObjectURL(
      new Blob([WORKLET_SOURCE], { type: "application/javascript" })
    );
    const pending = ctx.audioWorklet.addModule(url).finally(() => URL.revokeObjectURL(url));
    pending.catch(() => moduleLoads.delete(ctx));
    moduleLoads.set(ctx, pending);
  }
  return moduleLoads.get(ctx);
}

export function isAnswerDetectionSupported(ctx) {
  return !!(ctx && ctx.audioWorklet && typeof window.AudioWorkletNode === "function");
}

/**
 * Listen to the start of an answered call and report who picked up.
 *
 * @param {{
 *   ctx: AudioContext,
 *   remoteStream: MediaStream,
 *   micStream?: MediaStream | null,
 *   onDecision: (decision: { verdict: string, reason: string }) => void,
 *   config?: object,
 * }} opts
 * @returns {{ stop: () => void }}
 */
export function startHumanAnswerDetector({ ctx, remoteStream, micStream, onDecision, config }) {
  let stopped = false;
  const nodes = [];

  const stop = () => {
    if (stopped) return;
    stopped = true;
    nodes.forEach((node) => {
      try {
        node.disconnect();
      } catch (e) {
        // already disconnected
      }
    });
    const worklet = nodes.find((n) => n.port);
    if (worklet) worklet.port.onmessage = null;
  };

  if (!isAnswerDetectionSupported(ctx) || !remoteStream) {
    onDecision({ verdict: "unknown", reason: "unsupported" });
    return { stop };
  }

  const classifier = createAnswerClassifier(config);

  loadWorklet(ctx)
    .then(() => {
      if (stopped) return;
      const worklet = new window.AudioWorkletNode(ctx, PROCESSOR_NAME, {
        numberOfInputs: 2,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      const remoteSource = ctx.createMediaStreamSource(remoteStream);
      remoteSource.connect(worklet, 0, 0);
      nodes.push(remoteSource);
      if (micStream) {
        const micSource = ctx.createMediaStreamSource(micStream);
        micSource.connect(worklet, 0, 1);
        nodes.push(micSource);
      }
      const sink = ctx.createGain();
      sink.gain.value = 0;
      worklet.connect(sink);
      sink.connect(ctx.destination);
      nodes.push(worklet, sink);

      worklet.port.onmessage = (event) => {
        if (stopped) return;
        const decision = classifier.push(event.data);
        if (decision) {
          stop();
          onDecision(decision);
        }
      };
    })
    .catch((error) => {
      if (stopped) return;
      stop();
      onDecision({ verdict: "unknown", reason: "worklet_failed", error: error?.message });
    });

  return { stop };
}
